/**
 * Dashboard flow slice schema (#1337, program #1130 E2)
 *
 * Zod contract for GET /api/v1/dashboard/flow
 * (apps/edge-api/src/routes/dashboard-flow.ts) — the «Разговоры» tab of the
 * unified dashboard: how many conversations came in, where each of them is in
 * its life cycle, what was lost, and when the load peaks. One reader
 * (`AnalyticsRepository.getFlowSlice`, D1), one cohort — records by the
 * moment they came in (`created_at`).
 *
 * The life cycle is the funnel of «Главное» (`REPORT_FUNNEL_BUCKETS`), the
 * same classifier the records list's `outcome=` filter applies — so a bucket's
 * number and the list it opens are one set. The buckets are exclusive:
 * `buckets` summed with `unclassified` is `received`, in both windows.
 *
 * Windows are half-open, as the quality slice's (`dashboardSliceWindowSchema`):
 * a record exactly at `start` belongs to the current window only. The zone
 * cuts `days`, `hours` and `dayHour`.
 */

import { z } from 'zod/v4';
import { dashboardPeriodSchema } from './dashboard';
import {
  dashboardQualityScopeSchema,
  dashboardSliceWindowSchema,
} from './dashboard-quality';
import { REPORT_FUNNEL_BUCKETS } from './reports';
import { dashboardFlowChannelSchema, dashboardFlowDirectionSchema } from './common';

// ============================================================================
// Query
// ============================================================================

// The channel filter (`records.type`) lives in `common`, a leaf: `analytics`
// needs it too, and importing this module there would close a cycle through
// `reports`.

/**
 * Query of GET /api/v1/dashboard/flow — the quality slice's
 * (`dashboardQualityQuerySchema`: `period`, exact `periodStart`/`periodEnd`
 * overriding it, `includeDemo`, `timeZone`; `projectIds` and `tzOffset` read
 * beside it) plus `channel`. No channel — calls and chats.
 */
export const dashboardFlowQuerySchema = z.object({
  period: dashboardPeriodSchema.default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: z.enum(['yes', 'no']).default('yes'),
  timeZone: z.string().max(64).optional(),
  channel: dashboardFlowChannelSchema.optional(),
});

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();

/** Every bucket of the funnel, `missed` included — all required: a new route, no cache. */
export const dashboardFlowBucketsSchema = z.object(
  Object.fromEntries(
    REPORT_FUNNEL_BUCKETS.map((bucket) => [bucket, countSchema])
  ) as Record<(typeof REPORT_FUNNEL_BUCKETS)[number], typeof countSchema>
);

export const dashboardFlowTotalsSchema = z.object({
  /** Records that came in during the window. */
  received: countSchema,
  /** Where each of them is: exclusive, the funnel's classifier. */
  buckets: dashboardFlowBucketsSchema,
  /** No bucket took the record — the funnel's «Другие статусы». */
  unclassified: countSchema,
  /**
   * What could be analysed: `received − missed − too_short` — the
   * denominator of coverage (`analyzed / analysable`).
   */
  analysable: countSchema,
  /**
   * Missed calls with the inbound direction. «Missed» is the predicate
   * `call_category = 'missed'`, not the `missed` bucket: a missed call that
   * also has an analysis lies in `buckets.analyzed` and is still counted here.
   * Calls only (`type = 'call'`) — the channel `inboundCalls` counts.
   */
  missedInbound: countSchema,
  /** Missed calls with the outbound direction — the same predicate and channel. */
  missedOutbound: countSchema,
  /**
   * Inbound calls that were not missed (same predicate): the missed share is
   * `missedInbound / (inboundCalls + missedInbound)`.
   */
  inboundCalls: countSchema,
  /** Missed calls whose direction is not known — a number of their own, outside the share. */
  missedUnknownDirection: countSchema,
});

export const dashboardFlowDaySchema = z.object({
  /** Local calendar day `YYYY-MM-DD`, in the window's zone. */
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** The day as a half-open window `[start, end)`, clipped to the slice's own. */
  start: z.string().datetime(),
  end: z.string().datetime(),
  received: countSchema,
  analyzed: countSchema,
});

export const dashboardFlowChannelRowSchema = z.object({
  /** `records.type` — a string, not an enum: a new channel must not break the tab. */
  channel: z.string(),
  direction: dashboardFlowDirectionSchema,
  received: countSchema,
  analyzed: countSchema,
});

export const dashboardFlowCategoryRowSchema = z.object({
  /** `records.call_category`; `null` — not classified yet. */
  category: z.string().nullable(),
  received: countSchema,
  analyzed: countSchema,
});

export const dashboardFlowProjectRowSchema = z.object({
  projectId: z.string(),
  /** The project's name — configuration, no record content. */
  name: z.string().nullable(),
  received: countSchema,
  analyzed: countSchema,
});

export const dashboardFlowResponseSchema = z.object({
  metric: z.literal('flow.lifecycle'),
  /** The funnel classifier with `missed` as a bucket of its own (#1337). */
  metricVersion: z.literal('funnel_missed_v1'),
  source: z.literal('d1'),
  /** When the numbers were read — every request is read fresh. */
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  includeDemo: z.enum(['yes', 'no']),
  /** The channel counted; `null` — calls and chats. */
  channel: dashboardFlowChannelSchema.nullable(),
  /**
   * The whole current window has happened (`end <= asOf`). A calendar period
   * that is still going on is not complete: its `previous` is then cut to the
   * same stretch of the previous window (up to `previousEnd`), so the change
   * compares equal lengths, and a per-day average leaves the unfinished day out.
   */
  complete: z.boolean(),
  /**
   * Where the compared stretch of the previous window ends: `window.start`
   * when `complete`, earlier while the period is still going on.
   */
  previousEnd: z.string().datetime(),
  current: dashboardFlowTotalsSchema,
  previous: dashboardFlowTotalsSchema,
  // ---- current window only -------------------------------------------------
  /**
   * Every local day of the current window up to `asOf`, an empty day a zero;
   * `received` sums to `current.received`.
   */
  days: z.array(dashboardFlowDaySchema),
  /** Channel × direction, an unknown direction included. */
  channels: z.array(dashboardFlowChannelRowSchema),
  /** By category, the not yet classified included. */
  categories: z.array(dashboardFlowCategoryRowSchema),
  /** By project, received and analysed. */
  projects: z.array(dashboardFlowProjectRowSchema),
  /** Received by local hour, 0–23. */
  hours: z.array(countSchema).length(24),
  /** Received by local weekday × hour: `dayHour[0]` is Monday, `dayHour[6]` Sunday. */
  dayHour: z.array(z.array(countSchema).length(24)).length(7),
});

// ============================================================================
// Types
// ============================================================================

export type DashboardFlowChannel = z.infer<typeof dashboardFlowChannelSchema>;
export type DashboardFlowQuery = z.infer<typeof dashboardFlowQuerySchema>;
export type DashboardFlowDirection = z.infer<typeof dashboardFlowDirectionSchema>;
export type DashboardFlowTotals = z.infer<typeof dashboardFlowTotalsSchema>;
export type DashboardFlowDay = z.infer<typeof dashboardFlowDaySchema>;
export type DashboardFlowChannelRow = z.infer<typeof dashboardFlowChannelRowSchema>;
export type DashboardFlowCategoryRow = z.infer<typeof dashboardFlowCategoryRowSchema>;
export type DashboardFlowProjectRow = z.infer<typeof dashboardFlowProjectRowSchema>;
export type DashboardFlowResponse = z.infer<typeof dashboardFlowResponseSchema>;
